import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Repeat, Loader2, PauseCircle, PlayCircle, XCircle, Pencil, ShieldCheck, CheckCircle2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { getManageInfo, pauseGift, resumeGift, cancelGift, updateGiftAmount } from "../lib/api";

const STATE_META = {
  activating: { label: "Activating", cls: "bg-amber-100 text-amber-800", icon: Loader2 },
  active: { label: "Active", cls: "bg-teal/10 text-teal", icon: CheckCircle2 },
  paused: { label: "Paused", cls: "bg-amber-100 text-amber-800", icon: PauseCircle },
  ending: { label: "Ends at period end", cls: "bg-amber-100 text-amber-800", icon: CalendarClock },
  canceled: { label: "Canceled", cls: "bg-softgray/50 text-slate", icon: XCircle },
};

const ManageGift = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newAmount, setNewAmount] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await getManageInfo(token);
      setInfo(data);
      setNewAmount(String(data.amount || ""));
    } catch (e) {
      setError(e?.response?.data?.detail || "We couldn't find a monthly gift for this link.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { document.title = "Manage Your Monthly Gift | Iron Shield"; load(); }, [load]);

  const act = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      await load();
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  const saveAmount = () => {
    const amt = Number(newAmount);
    if (!amt || amt < 1) { toast.error("Enter an amount of at least $1."); return; }
    act(() => updateGiftAmount(token, amt), `Your monthly gift is now $${amt}.`);
  };

  const meta = info ? (STATE_META[info.state] || STATE_META.active) : null;
  const StateIcon = meta?.icon || CheckCircle2;

  return (
    <section className="min-h-[75vh] bg-offwhite pt-[130px] md:pt-[150px] pb-20" data-testid="manage-gift-page">
      <div className="container-x max-w-2xl">
        <div className="i-card p-8 md:p-10">
          <div className="flex items-center gap-2.5 text-navy">
            <Repeat className="h-5 w-5 text-gold" />
            <h1 className="font-heading text-2xl md:text-3xl font-black">Manage your monthly gift</h1>
          </div>

          {loading && <div className="mt-8 flex items-center gap-3 text-slate"><Loader2 className="h-5 w-5 animate-spin" /> Loading your gift…</div>}

          {!loading && error && (
            <div className="mt-8" data-testid="manage-error">
              <p className="text-charcoal/80">{error}</p>
              <Link to="/donate" className="mt-6 inline-flex items-center gap-2 bg-navy text-offwhite px-6 py-3 rounded-lg text-sm font-semibold hover:bg-navy-deep transition-colors">Back to donate</Link>
            </div>
          )}

          {!loading && info && (
            <div className="mt-8 space-y-7">
              {info.state === "activating" ? (
                <div className="rounded-lg border border-softgray bg-offwhite p-6" data-testid="manage-activating">
                  <p className="text-charcoal/80">Your monthly gift is still activating with our payment provider. Please refresh this page in a minute.</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-softgray bg-white p-5">
                    <div>
                      <div className="label-caps text-slate">Current gift</div>
                      <div className="mt-1 font-heading text-3xl font-black text-navy" data-testid="manage-amount">${info.amount}<span className="text-base font-semibold text-slate">/month</span></div>
                      {info.next_billing && info.state !== "canceled" && (
                        <div className="mt-1 text-sm text-slate">{info.state === "ending" ? "Ends on" : "Next charge"} {new Date(info.next_billing).toLocaleDateString()}</div>
                      )}
                    </div>
                    <span className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-bold ${meta.cls}`} data-testid="manage-state">
                      <StateIcon className={`h-4 w-4 ${info.state === "activating" ? "animate-spin" : ""}`} /> {meta.label}
                    </span>
                  </div>

                  {info.state !== "canceled" && (
                    <div className="space-y-4">
                      {/* Update amount */}
                      <div className="rounded-lg border border-softgray p-5">
                        <div className="flex items-center justify-between">
                          <div className="label-caps text-navy flex items-center gap-2"><Pencil className="h-3.5 w-3.5 text-gold" /> Change monthly amount</div>
                          {!editing && <button onClick={() => setEditing(true)} data-testid="manage-edit-btn" className="text-sm font-semibold text-navy hover:text-gold-deep transition-colors">Edit</button>}
                        </div>
                        {editing && (
                          <div className="mt-4 flex flex-col sm:flex-row gap-3">
                            <div className="relative flex-1">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate font-semibold">$</span>
                              <input type="number" min="1" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} data-testid="manage-amount-input" className="w-full rounded-lg border border-softgray bg-white pl-8 pr-4 py-3 text-charcoal focus:border-navy focus:outline-none focus:ring-2 focus:ring-navy/25" />
                            </div>
                            <button onClick={saveAmount} disabled={busy} data-testid="manage-save-btn" className="inline-flex items-center justify-center gap-2 bg-navy text-offwhite px-6 py-3 rounded-lg text-sm font-semibold hover:bg-navy-deep transition-colors disabled:opacity-60">
                              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Pause / Resume + Cancel */}
                      <div className="flex flex-wrap gap-3">
                        {info.state === "paused" ? (
                          <button onClick={() => act(() => resumeGift(token), "Your monthly gift has resumed. Thank you!")} disabled={busy} data-testid="manage-resume-btn" className="inline-flex items-center gap-2 bg-teal text-white px-5 py-3 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
                            <PlayCircle className="h-4 w-4" /> Resume gift
                          </button>
                        ) : info.state !== "ending" && (
                          <button onClick={() => act(() => pauseGift(token), "Your monthly gift is paused. Resume any time.")} disabled={busy} data-testid="manage-pause-btn" className="inline-flex items-center gap-2 border border-navy text-navy px-5 py-3 rounded-lg text-sm font-semibold hover:bg-navy hover:text-offwhite transition-colors disabled:opacity-60">
                            <PauseCircle className="h-4 w-4" /> Pause gift
                          </button>
                        )}
                        {info.state !== "ending" && (
                          <button onClick={() => { if (window.confirm("Cancel your monthly gift at the end of the current period?")) act(() => cancelGift(token), "Your gift will end at the current period. Thank you for your support."); }} disabled={busy} data-testid="manage-cancel-btn" className="inline-flex items-center gap-2 text-slate hover:text-charcoal px-5 py-3 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60">
                            <XCircle className="h-4 w-4" /> Cancel gift
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {info.state === "canceled" && (
                    <div className="rounded-lg border border-softgray bg-offwhite p-6">
                      <p className="text-charcoal/80">This monthly gift has been canceled. If you'd like to start giving again, we'd be grateful.</p>
                      <Link to="/donate" className="mt-5 inline-flex items-center gap-2 bg-gold text-charcoal px-6 py-3 rounded-lg text-sm font-bold hover:bg-gold-deep transition-colors">Give again</Link>
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center gap-2 text-xs text-slate pt-2 border-t border-softgray">
                <ShieldCheck className="h-4 w-4 text-gold" /> Billing is handled securely by Stripe. Keep this private link to manage your gift.
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ManageGift;
